import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { RefreshCw, Download, LogOut, Filter, Wifi, WifiOff, FileText, Plus, Edit, Trash2, AlertTriangle, ArrowLeft, Flag, Ban, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveResults = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [stages, setStages] = useState([]);
  const [classes, setClasses] = useState([]);
  const [driveTypes, setDriveTypes] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedDriveTypes, setSelectedDriveTypes] = useState([]);
  const [selectedClubmans, setSelectedClubmans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [appSettings, setAppSettings] = useState({ results_finalized: false });
  
  // Penalty dialog state
  const [showPenaltyDialog, setShowPenaltyDialog] = useState(false);
  const [newPenalty, setNewPenalty] = useState({ car_number: '', penalty_time: '', reason: '' });
  const [penalties, setPenalties] = useState([]);
  const [showPenaltiesView, setShowPenaltiesView] = useState(false);
  
  // Edit time dialog
  const [showEditTimeDialog, setShowEditTimeDialog] = useState(false);
  const [editingTime, setEditingTime] = useState(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchAppSettings();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchResults();
      fetchPenalties();
    }
  }, [selectedEvent, selectedClasses, selectedDriveTypes, selectedClubmans]);

  useEffect(() => {
    if (!autoRefresh || !selectedEvent) return;
    const interval = setInterval(() => fetchResults(false), 10000); // Silent auto-refresh
    return () => clearInterval(interval);
  }, [autoRefresh, selectedEvent, selectedClasses, selectedDriveTypes, selectedClubmans]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/app-settings`, getAuthHeaders());
      setAppSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch app settings');
    }
  };

  const updateAppSettings = async (newSettings) => {
    try {
      // Use dedicated finalize endpoint for results_finalized changes
      if ('results_finalized' in newSettings) {
        const response = await axios.post(
          `${API}/results/finalize?finalize=${newSettings.results_finalized}`, 
          null, 
          getAuthHeaders()
        );
        setAppSettings(response.data);
        toast.success(newSettings.results_finalized ? 'Results finalized' : 'Results reverted to provisional');
      } else {
        await axios.put(`${API}/app-settings`, newSettings, getAuthHeaders());
        setAppSettings(newSettings);
        toast.success('Settings updated');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const fetchEvents = async () => {
    try {
      const userRole = user.active_role || user.role;
      if (userRole === 'scorer' || userRole === 'marshal') {
        const response = await axios.get(`${API}/events/current`, getAuthHeaders());
        if (response.data) {
          setEvents([response.data]);
          setSelectedEvent(response.data);
        }
      } else {
        const response = await axios.get(`${API}/events`, getAuthHeaders());
        setEvents(response.data);
        const currentEvent = response.data.find(e => e.is_current);
        if (currentEvent) setSelectedEvent(currentEvent);
        else if (response.data.length > 0) setSelectedEvent(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch events');
    }
  };

  const fetchResults = async (showErrors = true) => {
    if (!selectedEvent) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ event_id: selectedEvent.id });
      // Support multiple class filters
      if (selectedClasses.length > 0) {
        selectedClasses.forEach(cls => params.append('class_filter', cls));
      }
      // Support multiple drive type filters
      if (selectedDriveTypes.length > 0) {
        selectedDriveTypes.forEach(dt => params.append('drive_type_filter', dt));
      }
      // Support multiple clubmans filters
      if (selectedClubmans.length > 0) {
        selectedClubmans.forEach(c => params.append('clubmans_filter', c));
      }
      
      const response = await axios.get(`${API}/live-results?${params}`, getAuthHeaders());
      setResults(response.data.results || []);
      setStages(response.data.stages || []);
      setClasses(response.data.classes || []);
      setDriveTypes(response.data.drive_types || []);
      setLastUpdated(new Date());
    } catch (error) {
      // Only show errors on manual refresh, not auto-refresh
      if (showErrors) {
        if (error.response?.status === 403) {
          toast.error('Live results are not enabled');
        } else {
          toast.error('Failed to fetch results');
        }
      }
      console.error('Fetch results error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPenalties = async () => {
    if (!selectedEvent) return;
    try {
      const response = await axios.get(`${API}/penalties?event_id=${selectedEvent.id}`, getAuthHeaders());
      setPenalties(response.data || []);
    } catch (error) {
      console.error('Failed to fetch penalties');
    }
  };

  const addPenalty = async () => {
    if (!newPenalty.car_number || !newPenalty.penalty_time) {
      toast.error('Car number and penalty time required');
      return;
    }
    try {
      await axios.post(`${API}/penalties`, {
        event_id: selectedEvent.id,
        car_number: newPenalty.car_number,
        penalty_time: newPenalty.penalty_time,
        reason: newPenalty.reason
      }, getAuthHeaders());
      toast.success('Penalty added');
      setShowPenaltyDialog(false);
      setNewPenalty({ car_number: '', penalty_time: '', reason: '' });
      fetchPenalties();
      fetchResults();
    } catch (error) {
      toast.error('Failed to add penalty');
    }
  };

  const deletePenalty = async (penaltyId) => {
    if (!window.confirm('Remove this penalty?')) return;
    try {
      await axios.delete(`${API}/penalties/${penaltyId}`, getAuthHeaders());
      toast.success('Penalty removed');
      fetchPenalties();
      fetchResults();
    } catch (error) {
      toast.error('Failed to remove penalty');
    }
  };

  const saveEditedTime = async () => {
    if (!editingTime) return;
    try {
      const params = new URLSearchParams({
        event_id: selectedEvent.id,
        stage_id: editingTime.stage_id,
        car_number: editingTime.car_number,
        edited_time: editingTime.edited_time,
        original_time: editingTime.original_time || '',
        reason: editingTime.reason || ''
      });
      await axios.post(`${API}/stage-time-edits?${params}`, null, getAuthHeaders());
      toast.success('Stage time updated');
      setShowEditTimeDialog(false);
      setEditingTime(null);
      fetchResults();
    } catch (error) {
      toast.error('Failed to update stage time');
    }
  };

  const updateEntryStatus = async (carNumber, newStatus) => {
    try {
      // Find the entry by car number (ensure string comparison)
      const response = await axios.get(`${API}/entry-list?event_id=${selectedEvent.id}`, getAuthHeaders());
      const entry = response.data.find(e => String(e.car_number) === String(carNumber));
      if (entry) {
        await axios.put(`${API}/entry-list/${entry.id}`, { entry_status: newStatus }, getAuthHeaders());
        toast.success(`Car #${carNumber} status updated to ${newStatus.toUpperCase()}`);
        fetchResults();
      } else {
        toast.error(`Entry not found for car #${carNumber}`);
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const cancelStage = async (stageId, cancel) => {
    const action = cancel ? 'cancel' : 'reinstate';
    if (!window.confirm(`Are you sure you want to ${action} this stage? ${cancel ? 'All times will show as "Cancelled".' : ''}`)) return;
    try {
      await axios.post(`${API}/stages/${stageId}/cancel?cancel=${cancel}`, null, getAuthHeaders());
      toast.success(`Stage ${cancel ? 'cancelled' : 'reinstated'} successfully`);
      fetchResults();
    } catch (error) {
      toast.error(`Failed to ${action} stage`);
    }
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams({ event_id: selectedEvent.id });
      // Support multiple filters
      if (selectedClasses.length > 0) {
        selectedClasses.forEach(cls => params.append('class_filter', cls));
      }
      if (selectedDriveTypes.length > 0) {
        selectedDriveTypes.forEach(dt => params.append('drive_type_filter', dt));
      }
      if (selectedClubmans.length > 0) {
        selectedClubmans.forEach(c => params.append('clubmans_filter', c));
      }
      
      const response = await axios.get(`${API}/live-results/export/excel?${params}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedEvent.name}_results.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Results exported');
    } catch (error) {
      toast.error('Failed to export results');
    }
  };

  const exportPDF = (isFinal = false) => {
    if (results.length === 0) {
      toast.error('No results to export');
      return;
    }
    
    const isFinalized = isFinal || appSettings.results_finalized;
    const documentTitle = isFinalized ? 'Official Final Results' : 'Official Provisional Results';
    
    // Build clean table HTML without dropdowns or edit markers
    const stageHeaders = stages.map(s => `<th style="background:#333;color:white;padding:5px;border:1px solid #ccc;text-align:center;">SS${s.number}</th>`).join('');
    
    const rows = results.map(r => {
      const stageTimeCells = stages.map(s => {
        const time = r.stage_times?.[`stage_${s.number}`] || '-';
        return `<td style="padding:5px;border:1px solid #ccc;text-align:center;font-family:monospace;">${time}</td>`;
      }).join('');
      
      const statusText = r.status === 'dnf' ? 'DNF' : r.status === 'dns' ? 'DNS' : 'Running';
      
      return `<tr>
        <td style="padding:5px;border:1px solid #ccc;font-weight:bold;">${r.position}</td>
        <td style="padding:5px;border:1px solid #ccc;font-family:monospace;font-weight:bold;">${r.car_number}</td>
        <td style="padding:5px;border:1px solid #ccc;">${r.driver}${r.co_driver ? '<br><small style="color:#666;">' + r.co_driver + '</small>' : ''}</td>
        <td style="padding:5px;border:1px solid #ccc;">${r.class_name}${r.clubmans ? ' (C)' : ''}</td>
        <td style="padding:5px;border:1px solid #ccc;">${r.drive_type}</td>
        <td style="padding:5px;border:1px solid #ccc;">${r.car_model || '-'}</td>
        ${stageTimeCells}
        <td style="padding:5px;border:1px solid #ccc;text-align:center;color:#f57c00;">${r.total_penalty !== '-' ? r.total_penalty : '-'}</td>
        <td style="padding:5px;border:1px solid #ccc;text-align:center;font-weight:bold;font-family:monospace;">${r.total_time}</td>
        <td style="padding:5px;border:1px solid #ccc;text-align:center;font-family:monospace;">${r.time_to_leader}</td>
        <td style="padding:5px;border:1px solid #ccc;text-align:center;">${statusText}</td>
      </tr>`;
    }).join('');
    
    // Signature section - COC for provisional, COC + MSA Steward for final
    const signatureSection = `
      <div style="margin-top:60px;page-break-inside:avoid;">
        <table style="width:100%;border:none;">
          <tr>
            <td style="width:50%;border:none;padding:20px;">
              <div style="border-bottom:1px solid #333;width:250px;margin-bottom:8px;"></div>
              <p style="margin:0;font-weight:bold;">Clerk of the Course (COC)</p>
              <p style="margin:5px 0 0 0;font-size:11px;color:#666;">Name: _______________________</p>
              <p style="margin:5px 0 0 0;font-size:11px;color:#666;">Date and Time: _______________________</p>
            </td>
            ${isFinalized ? `
            <td style="width:50%;border:none;padding:20px;">
              <div style="border-bottom:1px solid #333;width:250px;margin-bottom:8px;"></div>
              <p style="margin:0;font-weight:bold;">MSA Steward</p>
              <p style="margin:5px 0 0 0;font-size:11px;color:#666;">Name: _______________________</p>
              <p style="margin:5px 0 0 0;font-size:11px;color:#666;">Date and Time: _______________________</p>
            </td>
            ` : '<td style="width:50%;border:none;"></td>'}
          </tr>
        </table>
      </div>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${selectedEvent?.name || 'Rally'} - ${documentTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            h1 { color: #333; margin-bottom: 5px; }
            h2 { color: #333; margin-top: 0; font-size: 16px; }
            .filters { color: #666; font-size: 11px; margin-bottom: 15px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <h1>${selectedEvent?.name || 'Rally'}</h1>
          <h2>${documentTitle}</h2>
          <div class="filters">
            Generated: ${new Date().toLocaleString()}
            ${selectedClasses.length > 0 ? ` | Class: ${selectedClasses.join(', ')}` : ''}
            ${selectedDriveTypes.length > 0 ? ` | Drive: ${selectedDriveTypes.join(', ')}` : ''}
            ${selectedClubmans.length > 0 ? ` | Clubmans: ${selectedClubmans.join(', ')}` : ''}
          </div>
          <table>
            <thead>
              <tr>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;">Pos</th>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;">No.</th>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;">Driver/Co-Driver</th>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;">Class</th>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;">Drive</th>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;">Car</th>
                ${stageHeaders}
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;text-align:center;">Pen.</th>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;text-align:center;">Total</th>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;text-align:center;">Gap</th>
                <th style="background:#333;color:white;padding:5px;border:1px solid #ccc;text-align:center;">Status</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div style="margin-top:20px;font-size:10px;color:#666;">
            <p>(C) = Clubmans Entry</p>
          </div>
          ${signatureSection}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '-' || timeStr === 'DNS' || timeStr === 'DNF') return timeStr;
    return timeStr;
  };

  const userRole = user.active_role || user.role;
  const canEdit = userRole === 'admin' || userRole === 'scorer';

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* Header */}
      <div className="bg-[#18181b] border-b border-[#27272a] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Back Button */}
            <Button 
              onClick={() => {
                if (userRole === 'scorer') {
                  navigate('/records');
                } else if (userRole === 'admin') {
                  navigate('/admin');
                } else {
                  navigate('/');
                }
              }} 
              variant="ghost" 
              size="sm" 
              className="text-zinc-400 hover:text-white hover:bg-[#27272a]"
              data-testid="back-btn"
            >
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-xl font-black text-[#facc15] tracking-wider" data-testid="live-results-title">LIVE TIMING</h1>
              <p className="text-xs text-zinc-500">
                {lastUpdated ? `Updated: ${lastUpdated.toLocaleTimeString()}` : 'Loading...'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${isOnline ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
            </div>
            <NotificationBell user={user} />
            <Button onClick={onLogout} variant="ghost" size="sm" className="text-zinc-400" data-testid="logout-btn">
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-2 sm:p-4">
        {/* Controls */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-2 sm:p-4 mb-2 sm:mb-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
            {/* Event Selector */}
            {events.length > 1 ? (
              <select
                value={selectedEvent?.id || ''}
                onChange={(e) => setSelectedEvent(events.find(ev => ev.id === e.target.value))}
                className="bg-[#27272a] border-[#27272a] text-white rounded p-2 text-xs sm:text-sm w-full sm:w-auto"
                data-testid="event-select"
              >
                {events.map(event => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
            ) : (
              <div className="text-[#facc15] font-bold text-sm">{selectedEvent?.name || 'No Event'}</div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
              <Filter size={14} className="text-zinc-400 hidden sm:block" />
              
              {/* Class Filter - Multi-select */}
              <div className="relative group">
                <button
                  className="bg-[#27272a] border-[#27272a] text-white rounded p-1.5 sm:p-2 text-xs sm:text-sm min-w-[80px] text-left"
                  data-testid="class-filter"
                >
                  {selectedClasses.length === 0 ? 'Class' : `Class (${selectedClasses.length})`}
                </button>
                <div className="absolute hidden group-hover:block z-50 bg-[#27272a] border border-[#3f3f46] rounded shadow-lg mt-1 min-w-[150px] max-h-[200px] overflow-y-auto">
                  {classes.map(cls => (
                    <label key={cls} className="flex items-center gap-2 px-3 py-2 hover:bg-[#3f3f46] cursor-pointer text-xs">
                      <input
                        type="checkbox"
                        checked={selectedClasses.includes(cls)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedClasses([...selectedClasses, cls]);
                          } else {
                            setSelectedClasses(selectedClasses.filter(c => c !== cls));
                          }
                        }}
                        className="rounded bg-[#18181b] border-zinc-600"
                      />
                      {cls}
                    </label>
                  ))}
                  {selectedClasses.length > 0 && (
                    <button
                      onClick={() => setSelectedClasses([])}
                      className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-[#3f3f46] border-t border-[#3f3f46]"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Drive Type Filter - Multi-select */}
              <div className="relative group">
                <button
                  className="bg-[#27272a] border-[#27272a] text-white rounded p-1.5 sm:p-2 text-xs sm:text-sm min-w-[80px] text-left"
                  data-testid="drive-type-filter"
                >
                  {selectedDriveTypes.length === 0 ? 'Drive' : `Drive (${selectedDriveTypes.length})`}
                </button>
                <div className="absolute hidden group-hover:block z-50 bg-[#27272a] border border-[#3f3f46] rounded shadow-lg mt-1 min-w-[120px]">
                  {driveTypes.map(dt => (
                    <label key={dt} className="flex items-center gap-2 px-3 py-2 hover:bg-[#3f3f46] cursor-pointer text-xs">
                      <input
                        type="checkbox"
                        checked={selectedDriveTypes.includes(dt)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDriveTypes([...selectedDriveTypes, dt]);
                          } else {
                            setSelectedDriveTypes(selectedDriveTypes.filter(d => d !== dt));
                          }
                        }}
                        className="rounded bg-[#18181b] border-zinc-600"
                      />
                      {dt}
                    </label>
                  ))}
                  {selectedDriveTypes.length > 0 && (
                    <button
                      onClick={() => setSelectedDriveTypes([])}
                      className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-[#3f3f46] border-t border-[#3f3f46]"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Clubmans Filter - Multi-select */}
              <div className="relative group">
                <button
                  className="bg-[#27272a] border-[#27272a] text-white rounded p-1.5 sm:p-2 text-xs sm:text-sm min-w-[80px] text-left"
                  data-testid="clubmans-filter"
                >
                  {selectedClubmans.length === 0 ? 'Clubmans' : `Clubmans (${selectedClubmans.length})`}
                </button>
                <div className="absolute hidden group-hover:block z-50 bg-[#27272a] border border-[#3f3f46] rounded shadow-lg mt-1 min-w-[120px]">
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-[#3f3f46] cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={selectedClubmans.includes('yes')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedClubmans([...selectedClubmans, 'yes']);
                        } else {
                          setSelectedClubmans(selectedClubmans.filter(c => c !== 'yes'));
                        }
                      }}
                      className="rounded bg-[#18181b] border-zinc-600"
                    />
                    Clubmans
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-[#3f3f46] cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={selectedClubmans.includes('no')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedClubmans([...selectedClubmans, 'no']);
                        } else {
                          setSelectedClubmans(selectedClubmans.filter(c => c !== 'no'));
                        }
                      }}
                      className="rounded bg-[#18181b] border-zinc-600"
                    />
                    Non-Clubmans
                  </label>
                  {selectedClubmans.length > 0 && (
                    <button
                      onClick={() => setSelectedClubmans([])}
                      className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-[#3f3f46] border-t border-[#3f3f46]"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
              
              {/* Clear All Filters */}
              {(selectedClasses.length > 0 || selectedDriveTypes.length > 0 || selectedClubmans.length > 0) && (
                <button
                  onClick={() => {
                    setSelectedClasses([]);
                    setSelectedDriveTypes([]);
                    setSelectedClubmans([]);
                  }}
                  className="text-xs text-red-400 hover:text-red-300 px-2"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Auto Refresh Toggle - Hidden on mobile */}
            <label className="hidden sm:flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded bg-[#27272a] border-zinc-600"
              />
              Auto-refresh
            </label>

            {/* Actions */}
            <div className="flex flex-wrap gap-1 sm:gap-2 w-full sm:w-auto sm:ml-auto mt-2 sm:mt-0">
              <Button onClick={() => fetchResults(true)} variant="outline" size="sm" disabled={loading} className="border-zinc-700 px-2 sm:px-3" data-testid="refresh-btn">
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
              
              {canEdit && (
                <>
                  <Button 
                    onClick={() => setShowPenaltyDialog(true)} 
                    variant="outline" 
                    size="sm" 
                    className="border-orange-600 text-orange-400 hover:bg-orange-600/20 px-2 sm:px-3"
                    data-testid="add-penalty-btn"
                  >
                    <Plus size={14} />
                    <span className="hidden sm:inline ml-1">Penalty</span>
                  </Button>
                  <Button 
                    onClick={() => setShowPenaltiesView(!showPenaltiesView)} 
                    variant="outline" 
                    size="sm" 
                    className="border-zinc-700 px-2 sm:px-3"
                    data-testid="view-penalties-btn"
                  >
                    <AlertTriangle size={14} />
                    <span className="ml-1">{penalties.length}</span>
                  </Button>
                </>
              )}
              
              <Button onClick={exportExcel} variant="outline" size="sm" className="border-zinc-700 px-2 sm:px-3" data-testid="export-excel-btn">
                <Download size={14} />
                <span className="hidden sm:inline ml-1">Excel</span>
              </Button>
              <Button onClick={() => exportPDF(false)} variant="outline" size="sm" className="border-zinc-700 px-2 sm:px-3" data-testid="export-pdf-btn">
                <FileText size={14} />
                <span className="hidden sm:inline ml-1">PDF</span>
              </Button>
              {canEdit && (
                <Button 
                  onClick={() => {
                    if (!appSettings.results_finalized) {
                      if (window.confirm('Are you sure you want to finalize results? This will mark the results as official and final.')) {
                        updateAppSettings({ ...appSettings, results_finalized: true });
                        exportPDF(true);
                      }
                    } else {
                      exportPDF(true);
                    }
                  }}
                  variant="outline" 
                  size="sm" 
                  className={`px-2 sm:px-3 ${appSettings.results_finalized ? 'border-[#facc15] text-[#facc15] hover:bg-[#facc15]/20' : 'border-red-500 text-red-400 hover:bg-red-500/20'}`}
                  data-testid="finalize-btn"
                >
                  <Flag size={14} />
                  <span className="hidden sm:inline ml-1">{appSettings.results_finalized ? 'Final' : 'Finalize'}</span>
                </Button>
              )}
              {canEdit && appSettings.results_finalized && (
                <Button 
                  onClick={() => {
                    if (window.confirm('Revert to provisional results? This will allow further edits.')) {
                      updateAppSettings({ ...appSettings, results_finalized: false });
                    }
                  }}
                  variant="outline" 
                  size="sm" 
                  className="border-zinc-600 text-zinc-400 hover:bg-zinc-700"
                  data-testid="unfinalize-btn"
                >
                  Undo Finalize
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Penalties Panel (collapsible) */}
        {showPenaltiesView && penalties.length > 0 && (
          <div className="bg-[#18181b] border border-orange-600/50 rounded-lg p-4 mb-4">
            <h3 className="text-orange-400 font-bold mb-3">Penalties ({penalties.length})</h3>
            <div className="space-y-2">
              {penalties.map(penalty => (
                <div key={penalty.id} className="flex items-center justify-between bg-[#27272a] rounded p-2">
                  <div className="flex items-center gap-4">
                    <span className="text-[#facc15] font-mono font-bold">#{penalty.car_number}</span>
                    <span className="text-orange-400 font-mono">+{penalty.penalty_time}</span>
                    <span className="text-zinc-400 text-sm">{penalty.reason || 'No reason'}</span>
                  </div>
                  {canEdit && (
                    <Button 
                      onClick={() => deletePenalty(penalty.id)} 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Info Banner - Hidden on mobile */}
        {canEdit && (
          <div className="hidden sm:flex bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4 items-center gap-3">
            <Edit size={18} className="text-blue-400" />
            <div className="text-sm">
              <span className="text-blue-300 font-medium">Click any stage time</span>
              <span className="text-zinc-400"> to manually override it. Edited times appear with a </span>
              <span className="text-blue-400 bg-blue-500/20 px-1 rounded">blue highlight</span>
              <span className="text-zinc-400"> and </span>
              <span className="text-blue-400">✎</span>
              <span className="text-zinc-400"> icon.</span>
            </div>
          </div>
        )}

        {/* Results Table */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table id="results-table" className="w-full text-xs sm:text-sm" data-testid="results-table">
              <thead className="bg-[#27272a]">
                <tr>
                  <th className="px-1 sm:px-3 py-2 text-left text-[#facc15] font-bold text-xs">Pos</th>
                  <th className="px-1 sm:px-3 py-2 text-left text-[#facc15] font-bold text-xs">No.</th>
                  <th className="px-1 sm:px-3 py-2 text-left text-[#facc15] font-bold text-xs">Driver</th>
                  <th className="px-1 sm:px-3 py-2 text-left text-[#facc15] font-bold text-xs hidden sm:table-cell">Class</th>
                  <th className="px-1 sm:px-3 py-2 text-left text-[#facc15] font-bold text-xs hidden md:table-cell">Drive</th>
                  <th className="px-1 sm:px-3 py-2 text-left text-[#facc15] font-bold text-xs hidden lg:table-cell">Car</th>
                  {stages.map(stage => (
                    <th key={stage.id} className="px-1 sm:px-3 py-2 text-center text-[#facc15] font-bold whitespace-nowrap text-xs">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={stage.is_cancelled ? 'line-through text-red-400' : ''}>
                          SS{stage.number}
                        </span>
                        {stage.is_cancelled && <span className="text-[10px] text-red-400 font-normal">(Cancelled)</span>}
                        {canEdit && (
                          <button
                            onClick={() => cancelStage(stage.id, !stage.is_cancelled)}
                            className={`mt-0.5 p-0.5 rounded hover:bg-[#3f3f46] ${stage.is_cancelled ? 'text-green-400' : 'text-orange-400'}`}
                            title={stage.is_cancelled ? 'Reinstate stage' : 'Cancel stage'}
                            data-testid={`cancel-stage-${stage.id}`}
                          >
                            {stage.is_cancelled ? <RotateCcw size={12} /> : <Ban size={12} />}
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-1 sm:px-3 py-2 text-center text-[#facc15] font-bold text-xs hidden sm:table-cell">Pen.</th>
                  <th className="px-1 sm:px-3 py-2 text-center text-[#facc15] font-bold text-xs">Total</th>
                  <th className="px-1 sm:px-3 py-2 text-center text-[#facc15] font-bold text-xs hidden sm:table-cell">Gap</th>
                  <th className="px-1 sm:px-3 py-2 text-center text-[#facc15] font-bold text-xs hidden md:table-cell">Leader</th>
                  {canEdit && <th className="px-1 sm:px-3 py-2 text-center text-[#facc15] font-bold text-xs">Status</th>}
                </tr>
              </thead>
              <tbody>
                {results.length === 0 ? (
                  <tr>
                    <td colSpan={11 + stages.length + (canEdit ? 1 : 0)} className="px-3 py-8 text-center text-zinc-500">
                      No results available. Upload an entry list and record timing data to see results.
                    </td>
                  </tr>
                ) : (
                  results.map((result, idx) => (
                    <tr 
                      key={result.car_number} 
                      className={`border-t border-[#27272a] hover:bg-[#1f1f23] ${idx === 0 ? 'bg-[#facc15]/10' : ''} ${result.status === 'dnf' || result.status === 'dns' ? 'opacity-60' : ''}`}
                      data-testid={`result-row-${result.car_number}`}
                    >
                      <td className="px-1 sm:px-3 py-2 font-bold text-white text-xs">{result.position}</td>
                      <td className="px-1 sm:px-3 py-2 font-mono text-[#facc15] font-bold text-xs">{result.car_number}</td>
                      <td className="px-1 sm:px-3 py-2">
                        <div className="text-white font-medium text-xs sm:text-sm">{result.driver}</div>
                        {result.co_driver && <div className="text-zinc-500 text-xs hidden sm:block">{result.co_driver}</div>}
                      </td>
                      <td className="px-1 sm:px-3 py-2 hidden sm:table-cell">
                        <span className="px-1 sm:px-2 py-0.5 sm:py-1 bg-[#27272a] rounded text-xs">{result.class_name}</span>
                        {result.clubmans && <span className="ml-1 text-xs text-blue-400" title="Clubmans">C</span>}
                      </td>
                      <td className="px-1 sm:px-3 py-2 text-zinc-400 text-xs hidden md:table-cell">{result.drive_type}</td>
                      <td className="px-1 sm:px-3 py-2 text-zinc-400 text-xs hidden lg:table-cell">{result.car_model}</td>
                      {stages.map(stage => {
                        const stageKey = `stage_${stage.number}`;
                        const time = result.stage_times?.[stageKey] || '-';
                        const isEdited = result.stage_times_edited?.[stageKey];
                        return (
                          <td 
                            key={stage.id} 
                            className={`px-1 sm:px-3 py-2 text-center font-mono group relative text-xs ${
                              canEdit ? 'cursor-pointer hover:bg-[#3f3f46]' : ''
                            } ${isEdited ? 'bg-blue-500/20 text-blue-300 border-l-2 border-blue-400' : 'text-white'}`}
                            onClick={() => {
                              if (canEdit) {
                                setEditingTime({
                                  stage_id: stage.id,
                                  stage_number: stage.number,
                                  car_number: result.car_number,
                                  original_time: time,
                                  edited_time: time === '-' ? '' : time,
                                  reason: ''
                                });
                                setShowEditTimeDialog(true);
                              }
                            }}
                            title={canEdit ? 'Click to edit time' : ''}
                          >
                            <div className="flex items-center justify-center gap-0.5">
                              {formatTime(time)}
                              {isEdited && <span className="text-blue-400 text-xs">✎</span>}
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-1 sm:px-3 py-2 text-center font-mono text-orange-400 text-xs hidden sm:table-cell">
                        {result.total_penalty !== '-' ? result.total_penalty : '-'}
                      </td>
                      <td className="px-1 sm:px-3 py-2 text-center font-mono font-bold text-white text-xs">
                        {result.total_time}
                      </td>
                      <td className="px-1 sm:px-3 py-2 text-center font-mono text-zinc-400 text-xs hidden sm:table-cell">
                        {result.time_to_previous}
                      </td>
                      <td className="px-1 sm:px-3 py-2 text-center font-mono text-zinc-400 text-xs hidden md:table-cell">
                        {result.time_to_leader}
                      </td>
                      {canEdit && (
                        <td className="px-1 sm:px-3 py-2 text-center">
                          <select
                            value={result.status}
                            onChange={(e) => updateEntryStatus(result.car_number, e.target.value)}
                            className={`bg-[#27272a] text-xs rounded px-2 py-1 ${
                              result.status === 'dnf' ? 'text-red-400' : 
                              result.status === 'dns' ? 'text-yellow-400' : 
                              'text-green-400'
                            }`}
                          >
                            <option value="running">Running</option>
                            <option value="dnf">DNF</option>
                            <option value="dns">DNS</option>
                          </select>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500/20 rounded"></div>
            <span>Manually edited time</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-blue-400">C</span>
            <span>Clubmans entry</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-orange-400">+00:30</span>
            <span>Time penalty</span>
          </div>
        </div>
      </div>

      {/* Add Penalty Dialog */}
      <Dialog open={showPenaltyDialog} onOpenChange={setShowPenaltyDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#facc15]">Add Penalty</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Car Number</Label>
              <Input
                value={newPenalty.car_number}
                onChange={(e) => setNewPenalty({ ...newPenalty, car_number: e.target.value })}
                placeholder="e.g., 001"
                className="bg-[#27272a] border-[#27272a] text-white mt-1"
                data-testid="penalty-car-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Penalty Time (MM:SS)</Label>
              <Input
                value={newPenalty.penalty_time}
                onChange={(e) => setNewPenalty({ ...newPenalty, penalty_time: e.target.value })}
                placeholder="e.g., 00:30"
                className="bg-[#27272a] border-[#27272a] text-white mt-1"
                data-testid="penalty-time-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Reason (optional)</Label>
              <Input
                value={newPenalty.reason}
                onChange={(e) => setNewPenalty({ ...newPenalty, reason: e.target.value })}
                placeholder="e.g., Jump start"
                className="bg-[#27272a] border-[#27272a] text-white mt-1"
                data-testid="penalty-reason-input"
              />
            </div>
            <Button 
              onClick={addPenalty} 
              className="w-full bg-orange-600 hover:bg-orange-700"
              data-testid="submit-penalty-btn"
            >
              Add Penalty
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Time Dialog */}
      <Dialog open={showEditTimeDialog} onOpenChange={setShowEditTimeDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#facc15]">Edit Stage Time</DialogTitle>
          </DialogHeader>
          {editingTime && (
            <div className="space-y-4 py-4">
              <div className="bg-[#27272a] rounded p-3">
                <p className="text-zinc-400 text-sm">Car #{editingTime.car_number} • SS{editingTime.stage_number}</p>
                <p className="text-white">Original: <span className="font-mono">{editingTime.original_time || 'No time recorded'}</span></p>
              </div>
              <div>
                <Label className="text-zinc-300">New Time (MM:SS:ms)</Label>
                <Input
                  value={editingTime.edited_time}
                  onChange={(e) => setEditingTime({ ...editingTime, edited_time: e.target.value })}
                  placeholder="e.g., 05:32:450"
                  className="bg-[#27272a] border-[#27272a] text-white mt-1 font-mono"
                  data-testid="edit-time-input"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Reason for edit</Label>
                <Input
                  value={editingTime.reason}
                  onChange={(e) => setEditingTime({ ...editingTime, reason: e.target.value })}
                  placeholder="e.g., Timing error correction"
                  className="bg-[#27272a] border-[#27272a] text-white mt-1"
                  data-testid="edit-reason-input"
                />
              </div>
              <Button 
                onClick={saveEditedTime} 
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="save-edit-btn"
              >
                Save Edit
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveResults;
